import { useId } from "react";
import { cn } from "@/utils/cn";

export interface RangeSliderProps {
  question: string;
  leftLabel: string;
  rightLabel: string;
  value: number; // 0–100
  onChange: (value: number) => void;
  isPristine?: boolean; // true = never touched by user, show neutral appearance
  className?: string;
}

/** Returns the badge label + color style based on slider position */
function getBadge(value: number, leftLabel: string, rightLabel: string) {
  if (value < 50) {
    return {
      label: leftLabel,
      bg: "bg-primary-750/10 dark:bg-primary-900/35",
      border: "border-primary-750/20 dark:border-primary-350/50",
      text: "text-primary-750 dark:text-primary-350",
    };
  }
  if (value > 50) {
    return {
      label: rightLabel,
      bg: "bg-primary-purple/10 dark:bg-primary-900/35",
      border: "border-primary-purple/20 dark:border-primary-350/50",
      text: "text-primary-purple dark:text-primary-350",
    };
  }
  return {
    label: "Balanced",
    bg: "bg-neutral-100 dark:bg-[rgba(255,255,255,0.06)]",
    border: "border-secondary-200 dark:border-[rgba(255,255,255,0.1)]",
    text: "text-accent-250 dark:text-[rgba(255,255,255,0.4)]",
  };
}

export const RangeSlider = ({
  question,
  leftLabel,
  rightLabel,
  value,
  onChange,
  isPristine = false,
  className,
}: RangeSliderProps) => {
  const id = useId();
  const badge = getBadge(value, leftLabel, rightLabel);

  const sliderClass = isPristine
    ? "bg-range-slider-empty dark:bg-[linear-gradient(90deg,rgba(255,255,255,0.14)_0%,rgba(255,255,255,0.14)_100%)]"
    : "bg-range-slider-filled dark:bg-[linear-gradient(90deg,var(--color-primary-750)_0%,var(--color-primary-purple)_var(--slider-value),rgba(255,255,255,0.14)_var(--slider-value),rgba(255,255,255,0.14)_100%)]";
  const sliderStyle = isPristine ? undefined : ({ "--slider-value": `${value}%` } as React.CSSProperties);

  const mutedLabel = "text-secondary-400 dark:text-pab-dark-subtle-text";
  const activeEndLabel = isPristine
    ? mutedLabel
    : value > 50
      ? "text-primary-purple dark:text-primary-350"
      : mutedLabel;
  const activeStartLabel = isPristine
    ? mutedLabel
    : value < 50
      ? "text-primary-750 dark:text-primary-350"
      : mutedLabel;

  return (
    <div
      className={cn(
        "bg-white dark:bg-[rgba(255,255,255,0.03)] border border-secondary-200 dark:border-[rgba(255,255,255,0.07)] rounded-[14px] px-4 py-4 flex flex-col gap-[14px]",
        className,
      )}
    >
      {/* Question + badge */}
      <div className="flex items-start justify-between gap-3">
        <p className="text-14 text-secondary-900 dark:text-white leading-[1.375] flex-1">
          {question}
        </p>
        <span
          className={cn(
            "flex-shrink-0 inline-flex items-center justify-center px-[10px] h-[21px] rounded-full border text-[10px] font-semibold tracking-[0.12px] whitespace-nowrap",
            badge.bg,
            badge.border,
            badge.text,
          )}
        >
          {badge.label}
        </span>
      </div>

      {/* Track */}
      <div className="flex flex-col gap-[10px]">
        <input
          id={id}
          type="range"
          min={0}
          max={100}
          step={1}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className={cn("range-slider w-full", sliderClass, isPristine && "range-slider-pristine")}
          style={sliderStyle}
        />
        {/* Min / Max labels */}
        <div className="flex items-center justify-between">
          <span className={cn("text-[11px] font-semibold tracking-[0.065px]", activeStartLabel)}>
            {leftLabel}
          </span>
          <span className={cn("text-[11px] font-semibold tracking-[0.065px]", activeEndLabel)}>
            {rightLabel}
          </span>
        </div>
      </div>
    </div>
  );
};
